<template>
	<div class="container-fluid viewport-shell">
		<div
			class="d-flex justify-content-between align-items-center pt-4 mb-4 flex-shrink-0"
		>
			<h2>
				<i class="bi bi-crosshair me-2"></i>
				Correct Finding
			</h2>
			<div class="d-flex gap-2 align-items-center">
				<button
					class="btn btn-outline-primary btn-sm"
					:disabled="loading"
					@click="nextFinding"
					title="Next finding (N)"
				>
					<i class="bi bi-arrow-repeat me-1"></i>
					Next finding
					<kbd class="key-chip">N</kbd>
				</button>
				<RouterLink
					to="/_/sudo/vision-tasks"
					class="btn btn-outline-secondary btn-sm"
				>
					<i class="bi bi-arrow-left me-1"></i>
					Back
				</RouterLink>
				<button
					class="btn btn-outline-secondary btn-sm"
					title="Full instructions: mouse gestures and keyboard shortcuts (?)"
					@click="showShortcutsModal = true"
				>
					<i class="bi bi-question-circle me-1"></i>
					Instructions
					<kbd class="key-chip">?</kbd>
				</button>
			</div>
		</div>

		<div v-if="loading" class="text-center text-muted py-5">
			<div class="spinner-border text-primary mb-2" role="status"></div>
			<p class="small">Loading finding...</p>
		</div>

		<div v-else-if="error" class="alert alert-danger">{{ error }}</div>

		<div v-else-if="finding === null" class="text-center text-muted py-5">
			<p>All findings resolved.</p>
			<button class="btn btn-outline-primary btn-sm" @click="nextFinding">
				<i class="bi bi-arrow-repeat"></i> Refresh
			</button>
		</div>

		<div v-else-if="!finding.tile" class="alert alert-warning">
			Could not compute a centering tile for this finding.
		</div>

		<div v-else class="finding-layout">
			<div
				class="d-flex align-items-center gap-3 mb-3 text-muted flex-shrink-0"
			>
				<span>
					<RouterLink
						:to="`${BASE_PATH}/${finding.id}`"
						class="link-primary text-decoration-none"
						title="Link to this finding"
					>
						Finding #{{ finding.id }}
						<i class="bi bi-box-arrow-up-right"></i>
					</RouterLink>
				</span>
				<span
					class="badge"
					:class="
						finding.kind === 'false_negative'
							? 'text-bg-danger'
							: 'text-bg-warning'
					"
				>
					{{ finding.kind.replace("_", " ") }}
				</span>
				<span>{{ finding.status }}</span>
				<span v-if="finding.itemId" class="font-monospace" title="Planet item">
					Item {{ finding.itemId }}
				</span>
				<span>{{ formatDate(finding.createdAt) }}</span>
				<span
					>Tile {{ finding.tile.z }}/{{ finding.tile.x }}/{{
						finding.tile.y
					}}</span
				>
				<span v-if="finding" class="small">
					<span class="badge" :style="boxBadgeStyle">finding box</span>
					<span class="badge" style="background: #0d6efd; color: #fff"
						>existing pool</span
					>
					<span class="badge" style="background: #22c55e; color: #fff"
						>new pool</span
					>
				</span>
			</div>

			<!-- Review actions docked directly above the imagery, each shortcut
			     visible on its button; Esc (the only shortcut with no button)
			     is shown inline at the end of the row. The header ? button
			     opens the full instructions dialog. Same handlers, labels and
			     disabled conditions as the actions that used to sit in the top
			     header; this is layout + copy only. -->
			<div
				class="d-flex align-items-center gap-2 flex-shrink-0 mb-2 toolbar-row"
			>
				<button
					v-if="finding"
					class="btn btn-sm"
					:class="drawMode ? 'btn-warning' : 'btn-outline-warning'"
					:disabled="loading || saving"
					@click="toggleDrawMode"
					:title="drawButtonTitle"
				>
					<i class="bi bi-pencil-square me-1"></i>
					{{ drawButtonLabel }}
					<kbd class="key-chip">V</kbd>
				</button>
				<button
					v-if="finding"
					class="btn btn-primary btn-sm"
					:disabled="loading || saving || finding === null"
					@click="saveFinding"
					title="Save all staged shapes/edits and resolve this finding (S)"
				>
					<i class="bi bi-pin-map me-1"></i>
					Save
					<kbd class="key-chip">S</kbd>
				</button>
				<button
					v-if="finding"
					class="btn btn-outline-danger btn-sm"
					:disabled="
						loading ||
						saving ||
						drawMode ||
						(selectedPoolId === null && selectedDraftUid === null)
					"
					@click="deleteSelection"
					:title="
						selectedDraftUid !== null
							? 'Delete the selected drawn (staged) shape (D)'
							: 'Delete the selected pool (D)'
					"
				>
					<i class="bi bi-trash me-1"></i>
					{{ selectedDraftUid !== null ? "Delete shape" : "Delete pool" }}
					<kbd class="key-chip">D</kbd>
				</button>
				<button
					v-if="finding"
					class="btn btn-outline-secondary btn-sm"
					:disabled="
						loading ||
						saving ||
						(selectedPoolId === null && selectedDraftUid === null)
					"
					@click="clearSelection"
					title="Clear pool / staged-shape selection (C)"
				>
					<i class="bi bi-x-circle me-1"></i>
					Clear selection
					<kbd class="key-chip">C</kbd>
				</button>
				<button
					v-if="finding"
					class="btn btn-outline-primary btn-sm"
					:disabled="loading"
					@click="toggleOutline"
					title="Toggle finding/pool outline overlay (O)"
				>
					<i
						:class="(showOutline ? 'bi bi-eye-slash' : 'bi bi-eye') + ' me-1'"
					></i>
					{{ showOutline ? "Hide outline" : "Show outline" }}
					<kbd class="key-chip">O</kbd>
				</button>
				<!-- Esc is the only shortcut with no button of its own, so it
				     lives here inline with the actions instead of a separate
				     legend line duplicating the buttons' own chips. -->
				<span
					class="ms-2 small text-muted d-inline-flex align-items-center gap-1"
					title="Cancel the in-progress trace / clear selection"
				>
					<kbd class="key-chip">Esc</kbd>
					cancel / clear
				</span>
			</div>

			<div
				ref="panelsArea"
				:key="finding.id"
				class="panels-area"
				:style="{
					gap: PANEL_GAP + 'px',
					gridTemplateColumns:
						layout.mode === 'grid2x2'
							? `repeat(2, ${layout.size}px)`
							: layout.mode === 'row4'
								? `repeat(4, ${layout.size}px)`
								: undefined,
				}"
			>
				<div v-if="layout.mode === 'error'" class="text-center text-muted">
					<p class="mb-1">
						I'm sorry the display can not be viewed at this screen size
					</p>
					<p class="small mb-0">Try making the window larger.</p>
				</div>
				<template v-else>
					<div
						v-for="v in variants"
						:key="v.id"
						class="card border-0 shadow-sm"
						:style="{ width: layout.size + 'px' }"
					>
						<div class="card-header py-2">
							<strong>{{ v.label }}</strong>
						</div>
						<div
							class="tile-panel"
							:class="{
								'is-drawing': drawMode,
							}"
							:style="{
								width: layout.size + 'px',
								height: layout.size + 'px',
								aspectRatio: 'auto',
							}"
							@mousedown="onTileMouseDown"
						>
							<div class="tile-zoom" :style="zoomStyle">
								<img
									:src="tileURL(v.id)"
									alt=""
									class="tile-img"
									loading="lazy"
									@error="onImgError($event, v.id)"
								/>
								<svg
									v-if="
										showOutline &&
										(hasBox || hasDraft || poolOutlineViews.length > 0)
									"
									class="tile-overlay"
									viewBox="0 0 256 256"
									preserveAspectRatio="none"
								>
									<!-- Selection halo: a white ring under the selected pool's
									     colored stroke (no fill), so the chosen pool stands
									     apart from its neighbors on any imagery. -->
									<polygon
										v-if="selectionHaloPoints"
										:points="selectionHaloPoints"
										fill="none"
										stroke="#ffffff"
										:stroke-width="selectionHaloWidth"
										vector-effect="non-scaling-stroke"
									/>
									<!-- Existing pools in view (uniform color — this
									     interface does not deal with pool condition),
									     plus the finding's referenced pool when it has
									     one. The reviewer can select, move and reshape
									     them. No fill and non-scaling strokes: every
									     outline stays 2px on screen regardless of the
									     zoom transform, so the imagery under the
									     polygons stays usable as context. A pool being
									     reshaped hides its old geometry once the
									     replacement draft has points. -->
									<polygon
										v-for="p in poolOutlineViews"
										v-show="!poolHidden(p.id)"
										:key="p.id"
										:points="poolPointsString(p.id)"
										fill="none"
										:stroke="EXISTING_POOL_COLOR"
										:stroke-width="outlineStrokeWidth"
										:stroke-opacity="p.id === selectedPoolId ? 1 : 0.9"
										vector-effect="non-scaling-stroke"
									/>
									<polygon
										v-if="boxPoints"
										:points="boxPoints"
										fill="none"
										:stroke="findingBoxColor"
										:stroke-width="outlineStrokeWidth"
										vector-effect="non-scaling-stroke"
									/>
									<!-- Live trace: the in-progress shape between the start
									     click and the end click, trailing the cursor. -->
									<polygon
										v-if="tracePts.length > 0"
										:points="tracePointsString"
										fill="none"
										:stroke="NEW_POOL_COLOR"
										:stroke-width="outlineStrokeWidth"
										vector-effect="non-scaling-stroke"
									/>
									<!-- Staged closed shapes awaiting S: new pools (green)
									     drawn in this review session. Selectable + movable
									     when not drawing; D deletes one. A draft being
									     redrawn (V + trace over it) hides its old outline
									     until the replacement closes. -->
									<polygon
										v-for="s in stagedShapes"
										:key="s.uid"
										v-show="!stagedDraftHidden(s.uid)"
										:points="shapePointsString(s.pts)"
										fill="none"
										:stroke="NEW_POOL_COLOR"
										:stroke-width="outlineStrokeWidth"
										vector-effect="non-scaling-stroke"
									/>
								</svg>
							</div>
						</div>
					</div>
				</template>
			</div>
		</div>
	</div>

	<ToastNotification
		:show="toastShow"
		:title="toastTitle"
		:message="toastMessage"
	/>

	<Teleport to="body">
		<div v-if="showShortcutsModal">
			<div class="modal-backdrop fade show"></div>
			<div
				class="modal fade show d-block"
				tabindex="-1"
				@click.self="showShortcutsModal = false"
			>
				<div class="modal-dialog modal-dialog-centered">
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">
								<i class="bi bi-question-circle"></i>
								Instructions
							</h5>
							<button
								type="button"
								class="btn-close"
								@click="showShortcutsModal = false"
							></button>
						</div>
						<div class="modal-body">
							<h6 class="text-muted text-uppercase small fw-bold mb-2">
								Mouse
							</h6>
							<table class="table table-sm small mb-3">
								<tbody>
									<tr>
										<td class="text-nowrap">
											<i class="bi bi-cursor"></i> Click
										</td>
										<td>
											Select a pool or a drawn (staged) shape. When nothing is
											selected, click empty space to clear.
										</td>
									</tr>
									<tr>
										<td class="text-nowrap">
											<i class="bi bi-arrows-move"></i> Drag
										</td>
										<td>
											Move the selected pool or drawn shape (staged until Save).
											The finding box is fixed.
										</td>
									</tr>
									<tr>
										<td class="text-nowrap">
											<i class="bi bi-pencil-square"></i> Draw
										</td>
										<td>
											In draw mode: click to start a shape, move the mouse (no
											button held) to trace its outline, click again to finish.
											Each finished shape is staged; draw several, then Save.
										</td>
									</tr>
								</tbody>
							</table>
							<h6 class="text-muted text-uppercase small fw-bold mb-2">
								Colors
							</h6>
							<table class="table table-sm small mb-3">
								<tbody>
									<tr>
										<td>
											<span
												class="d-inline-block me-1"
												style="
													width: 0.9rem;
													height: 0.9rem;
													border-radius: 0.25rem;
													background: #dc3545;
													vertical-align: middle;
												"
											></span>
											<span
												class="d-inline-block"
												style="
													width: 0.9rem;
													height: 0.9rem;
													border-radius: 0.25rem;
													background: #ffc107;
													vertical-align: middle;
												"
											></span>
										</td>
										<td>
											Finding box — red for false positives, amber for false
											negatives.
										</td>
									</tr>
									<tr>
										<td>
											<span
												class="d-inline-block"
												style="
													width: 0.9rem;
													height: 0.9rem;
													border-radius: 0.25rem;
													background: #0d6efd;
													vertical-align: middle;
												"
											></span>
										</td>
										<td>Existing pool.</td>
									</tr>
									<tr>
										<td>
											<span
												class="d-inline-block"
												style="
													width: 0.9rem;
													height: 0.9rem;
													border-radius: 0.25rem;
													background: #22c55e;
													vertical-align: middle;
												"
											></span>
										</td>
										<td>Newly drawn pool (staged until Save).</td>
									</tr>
									<tr>
										<td>
											<span
												class="d-inline-block"
												style="
													width: 0.9rem;
													height: 0.9rem;
													border-radius: 0.25rem;
													background: #fff;
													border: 1px solid #adb5bd;
													vertical-align: middle;
												"
											></span>
										</td>
										<td>White halo — the current selection.</td>
									</tr>
								</tbody>
							</table>
							<h6 class="text-muted text-uppercase small fw-bold mb-2">
								Keyboard
							</h6>
							<table class="table table-sm small mb-0">
								<tbody>
									<tr>
										<td><kbd>O</kbd></td>
										<td>Toggle finding/pool outline overlay</td>
									</tr>
									<tr>
										<td><kbd>N</kbd></td>
										<td>Next finding</td>
									</tr>
									<tr>
										<td><kbd>S</kbd></td>
										<td>
											Save: commit all staged shapes and edits (drawn, moved,
											redrawn) and resolve the finding
										</td>
									</tr>
									<tr>
										<td><kbd>D</kbd></td>
										<td>
											Delete the selected pool, or discard the selected drawn
											(staged) shape (does not resolve the finding)
										</td>
									</tr>
									<tr>
										<td><kbd>V</kbd></td>
										<td>
											Draw — click to start a shape, move the mouse (no button
											held) to trace the outline, click again to finish and
											stage it. Draw several shapes, then S saves them all.
											Nothing selected: draw new pools. Pool selected: redraw
											its shape (keeps the pool). Staged shape selected: redraws
											that draft in place. V stops drawing but keeps staged
											shapes. When not drawing, staged shapes are
											click-selectable, drag-movable and deletable via D.
										</td>
									</tr>
									<tr>
										<td><kbd>C</kbd></td>
										<td>Clear pool / staged-shape selection</td>
									</tr>
									<tr>
										<td><kbd>?</kbd></td>
										<td>Show this instructions dialog</td>
									</tr>
									<tr>
										<td><kbd>Esc</kbd></td>
										<td>
											Close this dialog / cancel the current trace (draw mode) /
											clear selection
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
		</div>
	</Teleport>
</template>

<style scoped>
/* Viewport-locked shell (same pattern as rmo's mc-shell): the page fills the
   viewport so the four variant panels are always visible without scrolling. */
.viewport-shell {
	height: 100vh;
	height: 100dvh;
	display: flex;
	flex-direction: column;
}

/* Finding content fills the viewport below the toolbar; the meta row stays
   fixed and the panels area absorbs the remaining space. flex-basis: 0 keeps
   the panels area's height a pure function of the flex allocation — if the
   basis followed the content, the area's size would depend on the panel size
   it drives, feeding back into the ResizeObserver mid-resize. */
.finding-layout {
	flex: 1 1 0;
	min-height: 0;
	display: flex;
	flex-direction: column;
}

/* Panels area: lays the variant cards out from the upper-left in a fixed
   column count (2x2 grid or a single row of four), never scrolls. The column
   count and the inline `gap` are driven by the layout math so what is
   rendered always matches the computed mode. In error mode the single
   message div sits top-left on one column. */
.panels-area {
	flex: 1 1 0;
	min-height: 0;
	overflow: hidden;
	display: grid;
	justify-content: start;
	align-content: start;
	align-items: start;
}

.tile-panel {
	position: relative;
	width: 100%;
	aspect-ratio: 1 / 1;
	background: #e9ecef;
	/* The zoom wrapper scales the imagery beyond the panel's box; clip it so
	   each panel is an independent framed viewport and its content never
	   paints over (or steals pointer events from) neighboring panels. */
	overflow: hidden;
}
.tile-panel.is-drawing {
	cursor: crosshair;
}
.tile-img {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: fill;
}
.tile-overlay {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
}
/* Zoom wrapper: scales/translates the tile imagery (img + SVG overlay) so the
   flagged box fills 63% of the panel, centered. The transform is applied
   about the panel's top-left (origin 0 0); every pointer->tile conversion
   inverts it via eventToTilePoint. The wrapper is inset 0 so at scale 1 it
   covers the panel exactly. */
.tile-zoom {
	position: absolute;
	inset: 0;
	transform-origin: 0 0;
	will-change: transform;
}

/* Visible shortcut keys (toolbar buttons + legend + header): a light chip
   that inherits the surrounding color, so it reads cleanly on every button
   variant and on the muted legend — unlike Bootstrap's dark standalone <kbd>
   pill, which the shortcuts modal keeps. */
kbd.key-chip {
	font-family: var(--bs-font-monospace);
	font-size: 0.8em;
	font-weight: 600;
	line-height: 1;
	vertical-align: middle;
	color: inherit;
	background: rgba(33, 37, 41, 0.08);
	border: 1px solid rgba(33, 37, 41, 0.25);
	border-bottom-width: 2px;
	border-radius: 0.3rem;
	padding: 0.12rem 0.32rem;
	box-shadow: none;
}
.toolbar-row .btn {
	white-space: nowrap;
}
.btn .key-chip {
	margin-left: 0.35rem;
}
</style>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import { apiClient } from "@/client";
import ToastNotification from "@/components/ToastNotification.vue";
import type {
	ReviewFinding,
	VisionReviewTileDetail,
	VisionReviewTilePool,
} from "@/type/api";

interface ImageryVariant {
	id: string;
	label: string;
}

const variants: ImageryVariant[] = [
	{ id: "planet", label: "Planet (RGB)" },
	{ id: "blue-excess", label: "Blue Excess" },
	{ id: "ndwi", label: "NDWI" },
	{ id: "nir", label: "NIR" },
];

const TILE_PX = 256;
const BASE_PATH = "/_/sudo/vision-tasks/correct-finding";

// Outline colors, one per meaning. This interface never deals with pool
// condition, so condition colors are not used: the finding box is coded by
// finding kind (false positive = the model found a pool that is not there,
// so red; false negative = a real pool was missed, so amber), existing pools
// are one uniform color, and pools drawn in this session (trace + staged)
// are another. The selection halo is white and sits on top.
const BOX_COLOR_FALSE_POSITIVE = "#dc3545";
const BOX_COLOR_FALSE_NEGATIVE = "#ffc107";
const EXISTING_POOL_COLOR = "#0d6efd";
const NEW_POOL_COLOR = "#22c55e";

// Adaptive four-panel layout thresholds: below MIN_PANEL the tiles get
// squint-inducing; above MAX_PANEL the reviewer has to move their eyes too
// much between panels. MIN_PANEL is deliberately modest so the preferred 2x2
// grid stays feasible down to ordinary laptop-window heights — the single
// row of four is reserved for genuinely extreme vertical compression.
// PANEL_GAP must match the CSS gap on .panels-area.
const MIN_PANEL = 220;
const MAX_PANEL = 520;
const PANEL_GAP = 12;
// Fallback height (px) of the card-header strip above each tile panel
// (py-2 = 16px padding + 1rem line-height + 1px border-bottom). Each flex
// line consumes size + header, so the layout math subtracts the real
// (measured) header height; this constant only seeds the very first pass.
const CARD_HEADER_H = 41;

type PanelLayout = { mode: "grid2x2" | "row4" | "error"; size: number };
// size = square tile-panel edge in px (0 in error mode).
const layout = ref<PanelLayout>({ mode: "grid2x2", size: MIN_PANEL });
const panelsArea = ref<HTMLElement | null>(null);

const route = useRoute();
const router = useRouter();

const loading = ref(false);
const error = ref<string | null>(null);
const finding = ref<ReviewFinding | null>(null);
const brokenVariants = ref<Set<string>>(new Set());
const showOutline = ref(true);

// Existing pools intersecting the finding's imagery (context overlay), plus
// the finding's own flagged pool when it has one. Every pool polygon in view
// is selectable: the reviewer can delete it (D), move it (left-drag) or
// reshape it (V) — the finding box itself stays static.
const tilePools = ref<VisionReviewTilePool[]>([]);

// The currently selected pool id (click a pool polygon to select; click empty
// space or press C to clear). D / V / drag operations act on this selection.
const selectedPoolId = ref<number | null>(null);

// The currently selected staged draft's uid (click a staged green shape to
// select; mutually exclusive with selectedPoolId — drafts render above pools
// so they win hit-tests). A staged draft has no DB row yet, so it can be
// moved directly (its pts are what S POSTs/PUTs) but never D-deleted.
const selectedDraftUid = ref<number | null>(null);

// Staged pool moves: pool id -> its full outer ring translated to tile-pixel
// space (0..256). Filled live while the reviewer drags a selected pool and
// kept until S flushes them with PUT /api/vision/train/pool/{id}.
const stagedMoves = ref(new Map<number, { x: number; y: number }[]>());

// Pools deleted in this session (D). They are removed from the overlay and
// from hit-testing immediately; the finding's own data (display tile,
// framing) is left untouched so the imagery does not jump mid-session.
const deletedPoolIds = ref(new Set<number>());

// An in-progress move drag (pointer down inside the selected pool or a
// staged draft). A click (no travel) selects without moving; once the pointer
// travels past the threshold the outline follows it live — a pool through
// stagedMoves (PUT on save), a staged draft by translating its pts in place
// (the moved pts are what S sends). basePts is the shape's ring at drag start
// (already including any earlier staged move / prior draft geometry), and the
// tile delta is derived from CSS client-coordinate travel / (k*s).
interface MoveDragState {
	kind: "pool" | "draft";
	id: number;
	panel: HTMLElement;
	startClientX: number;
	startClientY: number;
	basePts: { x: number; y: number }[];
	moved: boolean;
}
const moveDrag = ref<MoveDragState | null>(null);
// CSS-pixel travel before a pool/draft click becomes a move drag (a plain
// click selects instead of nudging it).
const MOVE_DRAG_THRESHOLD = 3;

// Draw mode: toggled with V. The gesture is click to start a shape, move the
// (button-free) mouse to trace a smooth freehand outline, click again to
// close + stage it, and repeat — several shapes can be staged before S saves
// them all. The FIRST trace of a session replaces the selection when one is a
// pool (S PUTs, keeping id + properties) or a staged draft (its pts are
// swapped in place); every later shape in the session is a brand-new pool (S
// POSTs it).
const drawMode = ref(false);

// The live in-progress trace (tile-pixel space 0..256): points between the
// start click and the end click. Rendered green while tracing; emptied when a
// closed trace (>= 3 points) moves into stagedShapes on the end click.
const tracePts = ref<{ x: number; y: number }[]>([]);

interface StagedShape {
	uid: number;
	targetPoolId: number | null;
	pts: { x: number; y: number }[];
}

// Closed shapes awaiting S. targetPoolId null = brand-new pool (POST on save);
// a pool id = reshape replacement (PUT on save, keeps id/properties). uid is a
// monotonic counter used as the v-for key and to select the shape. Shapes
// survive V toggles and accumulate until S (or loadFinding) clears them. When
// not drawing they are click-selectable, drag-movable (pts translate in
// place), redrawable via V (pts swapped on completion) and deletable via D
// over the selection.
const stagedShapes = ref<StagedShape[]>([]);

// The target the CURRENT trace replaces when it closes, or null (a new pool).
// Captured when draw mode turns on from the selection — a pool (PUT on save)
// or a staged draft (same staged entry, pts swapped) — and cleared on any
// completion, so subsequent shapes in the session are new pools.
type DraftTarget =
	| { kind: "pool"; poolId: number }
	| { kind: "draft"; uid: number };
const draftTarget = ref<DraftTarget | null>(null);

// --- Draw gesture state machine (draw mode only) ---
// The machine is click/click: a click (press + release with no meaningful
// movement) either STARTS a trace (idle -> tracing) or ENDS one (tracing ->
// idle, staging the closed shape). While tracing, window mousemove events
// append freehand samples ~SAMPLE_STEP css px apart. This state is plain
// (non-reactive) and driven imperatively by the handlers.
type DrawState = "idle" | "tracing";
let drawState: DrawState = "idle";

// The panel + raw client coords of the current left-button press in draw
// mode, so the release can classify click (start/end) vs drag (ignored).
interface DrawPress {
	panel: HTMLElement;
	clientX: number;
	clientY: number;
	moved: boolean;
}
let press: DrawPress | null = null;

// The panel a live trace samples through (all four panels share the same zoom
// transform, so conversion is panel-independent; kept for the rect math).
let tracePanel: HTMLElement | null = null;

// Raw client coords of the last appended sample, so the sampling threshold
// measures true CSS-pixel travel (independent of the tile zoom).
let lastSampleClient: { x: number; y: number } | null = null;

let uidCounter = 0;

// CSS-pixel travel that turns a press into a drag — a click (start/end of a
// shape) must not have moved meaningfully past this.
const CLICK_MOVE_DIST = 6;
// CSS-pixel travel between freehand samples: ~2 px keeps curves smooth while
// coalesced mousemove events keep the vertex count bounded.
const SAMPLE_STEP = 2;

// Clamp a tile-pixel point into the 0..256 tile image.
function clampPos(p: { x: number; y: number }): { x: number; y: number } {
	return {
		x: Math.min(256, Math.max(0, p.x)),
		y: Math.min(256, Math.max(0, p.y)),
	};
}

const saving = ref(false);
const showShortcutsModal = ref(false);
const toastShow = ref(false);
const toastTitle = ref("");
const toastMessage = ref("");

function showToast(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
	setTimeout(() => {
		toastShow.value = false;
	}, 5000);
}

function tileURL(variantId: string): string {
	const t = displayTile.value;
	if (!t) return "";
	const baseURLs: Record<string, string> = {
		planet: "/api/tile/planet/any/{z}/{x}/{y}?bands=3,2,1",
		"blue-excess": "/api/tile/planet/any/{z}/{x}/{y}?transform=blue-excess",
		ndwi: "/api/tile/planet/any/{z}/{x}/{y}?transform=ndwi",
		nir: "/api/tile/planet/any/{z}/{x}/{y}?band=4&brightness=auto",
	};
	let url = baseURLs[variantId]
		.replace("{z}", String(t.z))
		.replace("{x}", String(t.x))
		.replace("{y}", String(t.y));
	if (t.offset) {
		url += "&offset";
	}
	if (finding.value?.itemId) {
		url += `&source=${encodeURIComponent(finding.value.itemId)}`;
	}
	return url;
}

function collectRings(geom: {
	type: string;
	coordinates: unknown;
}): [number, number][][] {
	const coords = geom.coordinates as unknown;
	if (geom.type === "Polygon") {
		return (coords as [number, number][][]) ?? [];
	}
	if (geom.type === "MultiPolygon") {
		const polys = coords as [number, number][][][];
		if (!polys || polys.length === 0) return [];
		return polys[0] ?? [];
	}
	return [];
}

// Fractional Web-Mercator slippy tile coordinates (the raw x/y used by
// projectToTile before the 0..256 px re-basing). Shared by the projection and
// by the display-tile picker below.
function tileXFrac(lng: number, z: number): number {
	return ((lng + 180) / 360) * Math.pow(2, z);
}

function tileYFrac(lat: number, z: number): number {
	const latRad = (lat * Math.PI) / 180;
	return (
		((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) *
		Math.pow(2, z)
	);
}

// Project a geographic point into tile pixel space (0..256) using the same
// Web-Mercator math as PositionInferenceAnchoring's outlinePoints. An offset
// tile centers on the corner at fractional (x+1, y+1) — the corner sits at the
// 256px image center — so the projection shifts and re-bases accordingly.
function projectToTile(
	lng: number,
	lat: number,
	tile: { z: number; x: number; y: number; offset?: boolean },
): { x: number; y: number } {
	const x = tileXFrac(lng, tile.z);
	const y = tileYFrac(lat, tile.z);
	if (tile.offset) {
		return {
			x: (x - (tile.x + 1)) * TILE_PX + TILE_PX / 2,
			y: (y - (tile.y + 1)) * TILE_PX + TILE_PX / 2,
		};
	}
	return { x: (x - tile.x) * TILE_PX, y: (y - tile.y) * TILE_PX };
}

type TileSpec = { z: number; x: number; y: number; offset?: boolean };

// Choose the offset tile whose data window fully contains the flagged box and
// its 63% framing window, so the crop never overruns the imagery. The API's
// tile centers on the grid corner nearest the geometry centroid, which can
// leave the box near the data edge; when the 63% window would spill past the
// data (a blank band), this picks a display tile whose centered corner leaves
// enough margin:
//   - Zoom up (z0..z0+3) first: the offset endpoint walks the item's pyramid,
//     so z18+ tiles show the same native data with a finer corner grid (the
//     window position granularity is what changes, not sharpness). The first
//     feasible zoom is used (smallest, so the window is largest).
//   - When no finer zoom can frame the box (it is too large relative to the
//     tile), fall back to z0-1 / z0-2 — a coarser tile gives the window more
//     relative room at the cost of sharpness.
//   - If nothing fits (pathological), null: callers fall back to the finding
//     tile and today's behavior.
// Feasibility is evaluated in tile-pixel units (256 px per tile on both axes,
// so Mercator latitude distortion is already baked in): the box's larger
// dimension, scaled to fill 63% of the 256px frame (the computeFraming crop),
// must fit inside the half-tile margin that surrounds the centered corner.
function pickDisplayTile(f: ReviewFinding): TileSpec | null {
	const t = f.tile;
	if (!t) return null;
	const geom = (f.pool?.geometry ?? f.geometry) as
		| { type: string; coordinates: unknown }
		| null
		| undefined;
	if (!geom) return null;
	const rings = collectRings(geom);
	if (rings.length === 0) return null;

	// Candidate corner + feasibility at a zoom. Returns the corner to center
	// on, or null when the 63% window does not fit inside the tile data.
	const feasibleAt = (
		z: number,
	): { cornerX: number; cornerY: number } | null => {
		let minX = Infinity;
		let minY = Infinity;
		let maxX = -Infinity;
		let maxY = -Infinity;
		for (const ring of rings) {
			for (const [lng, lat] of ring) {
				const fx = tileXFrac(lng, z);
				const fy = tileYFrac(lat, z);
				if (fx < minX) minX = fx;
				if (fx > maxX) maxX = fx;
				if (fy < minY) minY = fy;
				if (fy > maxY) maxY = fy;
			}
		}
		const boxW = (maxX - minX) * TILE_PX;
		const boxH = (maxY - minY) * TILE_PX;
		// Half of the 63% crop window, in tile px. When it reaches half the
		// tile the crop is the whole tile (computeFraming clamps s to 1) and
		// no centered window can fit — the box is too large for this zoom.
		const halfWindow = Math.max(boxW, boxH) / (2 * 0.63);
		if (halfWindow >= TILE_PX / 2) return null;
		const cx = (minX + maxX) / 2;
		const cy = (minY + maxY) / 2;
		const cornerX = Math.round(cx);
		const cornerY = Math.round(cy);
		// The centered corner sits at the image center (px 128); the window
		// must not cross the image edge: |center offset| + halfWindow <= 128
		// along both axes.
		if (Math.abs((cx - cornerX) * TILE_PX) + halfWindow > TILE_PX / 2)
			return null;
		if (Math.abs((cy - cornerY) * TILE_PX) + halfWindow > TILE_PX / 2)
			return null;
		return { cornerX, cornerY };
	};

	const z0 = t.z;
	let chosen: { z: number; cornerX: number; cornerY: number } | null = null;
	for (let z = z0; z <= z0 + 3; z++) {
		const c = feasibleAt(z);
		if (c) {
			chosen = { z, cornerX: c.cornerX, cornerY: c.cornerY };
			break;
		}
	}
	if (!chosen) {
		for (let z = z0 - 1; z >= Math.max(1, z0 - 2); z--) {
			const c = feasibleAt(z);
			if (c) {
				chosen = { z, cornerX: c.cornerX, cornerY: c.cornerY };
				break;
			}
		}
	}
	if (!chosen) return null;
	// Mirror OffsetTileForCentroid's clamping, then request the offset tile
	// whose centered corner is (cornerX, cornerY): the offset tile at (x, y)
	// centers the corner at fractional (x+1, y+1).
	const n = Math.pow(2, chosen.z);
	const cx = Math.min(n - 1, Math.max(1, chosen.cornerX));
	const cy = Math.min(n - 1, Math.max(1, chosen.cornerY));
	return { z: chosen.z, x: cx - 1, y: cy - 1, offset: true };
}

// Project a GeoJSON geometry's outer ring into tile-pixel points (0..256) for
// the given display tile, dropping a repeated closing vertex so the model has
// one point per corner. Returns null when the geometry has no ring.
function geometryToTilePoints(
	geom: { type: string; coordinates: unknown } | null | undefined,
	t: TileSpec,
): { x: number; y: number }[] | null {
	if (!geom) return null;
	const rings = collectRings(geom);
	if (rings.length === 0) return null;
	let pts = rings[0].map(([lng, lat]) => projectToTile(lng, lat, t));
	if (pts.length > 1) {
		const first = pts[0];
		const last = pts[pts.length - 1];
		if (Math.abs(last.x - first.x) < 0.5 && Math.abs(last.y - first.y) < 0.5) {
			pts = pts.slice(0, -1);
		}
	}
	return pts;
}

// The static finding box (marked area): the finding's own geometry, colored
// by finding kind (red false positive, amber false negative) and never edited
// by selection/move/reshape — it is the focus
// indicator the imagery is framed on. Rendered from the raw finding geometry
// every time, so it stays put no matter what pool edits are staged.
const boxPoints = computed(() => {
	const t = displayTile.value;
	const f = finding.value;
	if (!t || !f) return "";
	const pts = geometryToTilePoints(
		f.geometry as { type: string; coordinates: unknown } | null,
		t,
	);
	return pts === null
		? ""
		: pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
});
const hasBox = computed(() => boxPoints.value.length > 0);

// "x.x,y.x" polygon points string for a tile-pixel ring (shared by the live
// trace and every staged shape).
function shapePointsString(pts: { x: number; y: number }[]): string {
	return pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
}

// The live in-progress trace polygon (green): rendered while the pointer is
// tracing a shape between the start and end clicks.
const tracePointsString = computed(() => shapePointsString(tracePts.value));

// Any drawn geometry — the live trace or at least one staged shape. Drives
// the overlay svg v-if so drawing stays visible in all four panels.
const hasDraft = computed(
	() => tracePts.value.length > 0 || stagedShapes.value.length > 0,
);

// Every pool polygon in view — the tile context pools plus the finding's own
// referenced pool (false positives with a pool id). All are one uniform color
// (this interface never deals with condition), rendered together so
// hit-testing, move-drag staging and rendering share one source. A reshape
// replacement (staged shape or live trace) hides the pool's outline once it
// has points; the pool stays flagged hidden until the replacement is saved,
// removed or cancelled.
interface PoolOutlineView {
	id: number;
	basePts: { x: number; y: number }[];
}

const poolOutlineViews = computed<PoolOutlineView[]>(() => {
	const t = displayTile.value;
	if (!t) return [];
	const views: PoolOutlineView[] = [];
	for (const pool of tilePools.value) {
		if (deletedPoolIds.value.has(pool.id)) continue;
		const basePts = geometryToTilePoints(
			pool.geometry as { type: string; coordinates: unknown } | null,
			t,
		);
		if (basePts === null) continue;
		views.push({ id: pool.id, basePts });
	}
	// The finding's referenced pool is drawn last so it sits above context
	// pools (same uniform color; no separate meaning in this interface).
	const f = finding.value;
	if (
		f?.poolId != null &&
		!deletedPoolIds.value.has(f.poolId) &&
		f.pool?.geometry
	) {
		const basePts = geometryToTilePoints(
			f.pool.geometry as { type: string; coordinates: unknown } | null,
			t,
		);
		if (basePts !== null) {
			views.push({ id: f.poolId, basePts });
		}
	}
	return views;
});

// A reshape target's old outline hides while the pool has a staged
// replacement shape or a live trace in progress; it reappears when that shape
// is discarded (D) or the trace is cancelled without closing.
function poolHidden(poolId: number): boolean {
	return (
		stagedShapes.value.some((s) => s.targetPoolId === poolId) ||
		(draftTarget.value?.kind === "pool" &&
			draftTarget.value.poolId === poolId &&
			tracePts.value.length > 0)
	);
}

// Mirror of poolHidden for staged drafts: a staged draft being replaced by a
// live trace (V + draw over the selected draft) hides its old outline once
// the trace starts, exactly like a pool being reshaped.
function stagedDraftHidden(uid: number): boolean {
	return (
		draftTarget.value?.kind === "draft" &&
		draftTarget.value.uid === uid &&
		tracePts.value.length > 0
	);
}

// The ring a pool currently displays: the staged move when one exists (a
// left-drag is in progress or awaiting S), else its original geometry.
function poolDisplayPts(poolId: number): { x: number; y: number }[] {
	const view = poolOutlineViews.value.find((p) => p.id === poolId);
	if (!view) return [];
	return stagedMoves.value.get(poolId) ?? view.basePts;
}

function poolPointsString(poolId: number): string {
	return poolDisplayPts(poolId)
		.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
		.join(" ");
}

// Selection halo: a white ring just wider than the 2px colored stroke, so the
// selected pool stands out without filling it. SVG widths render through the
// zoom transform (vector-effect cancels only the viewBox stretch), so like
// outlineStrokeWidth this is the inverse of the zoom.
const selectionHaloWidth = computed(() =>
	String((6 / framing.value.s).toFixed(4)),
);

const selectionHaloPoints = computed(() => {
	const poolId = selectedPoolId.value;
	if (poolId !== null) {
		if (poolHidden(poolId)) return "";
		const view = poolOutlineViews.value.find((p) => p.id === poolId);
		if (!view) return "";
		return poolPointsString(poolId);
	}
	// A selected staged draft gets the same white halo ring around its pts.
	const uid = selectedDraftUid.value;
	if (uid === null) return "";
	if (stagedDraftHidden(uid)) return "";
	const shape = stagedShapes.value.find((s) => s.uid === uid);
	if (!shape) return "";
	return shapePointsString(shape.pts);
});

// The tile whose imagery is actually shown, chosen client-side by
// pickDisplayTile so the flagged box plus its 63% framing window always fit
// inside the offset tile's data. The finding's API tile centers on the grid
// corner nearest the geometry's centroid and can leave the box so close to
// the data edge that the 63% crop overruns into a blank band; the display
// tile recenters (and re-zooms only when needed) on the box itself. Every
// projection in this view — framing, outlines, pool geometry, drafts, and the
// save path — runs through this tile so drawn geometry stays aligned with the
// displayed imagery. Falls back to the finding's tile when no feasible display
// tile exists.
const displayTile = computed<TileSpec | null>(() => {
	const f = finding.value;
	if (!f) return null;
	return pickDisplayTile(f) ?? f.tile ?? null;
});

// Per-finding zoom framing in tile-pixel space (0..256). The flagged box —
// the referenced pool geometry when the finding has one, else the drawn area
// (the same centering rule reviewFindingToResponse uses) — is centered and its
// larger dimension fills 63% of the frame. When the box is already that large
// or larger the scale clamps to 1: no zoom-out, the tile is the whole imagery.
// The framing is size-independent (tile units); zoomStyle converts it into
// panel px per layout.size.
const framing = ref({ s: 1, tx: 0, ty: 0 });

function computeFraming() {
	const f = finding.value;
	const t = displayTile.value;
	if (!f || !t) return;
	const geom = (f.pool?.geometry ?? f.geometry) as
		| { type: string; coordinates: unknown }
		| null
		| undefined;
	if (!geom) {
		framing.value = { s: 1, tx: 0, ty: 0 };
		return;
	}
	const rings = collectRings(geom);
	let minX = Infinity;
	let minY = Infinity;
	let maxX = -Infinity;
	let maxY = -Infinity;
	for (const ring of rings) {
		for (const [lng, lat] of ring) {
			const p = projectToTile(lng, lat, t);
			if (p.x < minX) minX = p.x;
			if (p.x > maxX) maxX = p.x;
			if (p.y < minY) minY = p.y;
			if (p.y > maxY) maxY = p.y;
		}
	}
	if (!isFinite(minX)) {
		framing.value = { s: 1, tx: 0, ty: 0 };
		return;
	}
	const boxW = maxX - minX;
	const boxH = maxY - minY;
	const boxCx = (minX + maxX) / 2;
	const boxCy = (minY + maxY) / 2;
	// The box's larger dimension fills 63% of the 256px frame: zoom in when the
	// box is smaller than that (s > 1), but never zoom out — a box that already
	// fills 63% or more of the tile stays at scale 1 (the tile is the whole
	// imagery; the operator's "shrink to 0% buffer when the box is very large"
	// rule). The epsilon keeps a degenerate (point-sized) box from dividing by
	// zero.
	const s = Math.max(1, (0.63 * TILE_PX) / Math.max(boxW, boxH, 0.001));
	framing.value = {
		s,
		tx: TILE_PX / 2 - boxCx * s,
		ty: TILE_PX / 2 - boxCy * s,
	};
}

// The SVG polygon strokes are painted through the zoom wrapper's CSS scale:
// vector-effect cancels the viewBox stretch but NOT ancestor CSS transforms
// (measured ~2*s px on screen), so the SVG stroke-width must be the inverse
// of the zoom to render a constant 2px on screen at every zoom level.
const outlineStrokeWidth = computed(() =>
	String((2 / framing.value.s).toFixed(4)),
);

// The finding box color is set by finding kind — false positive (the model
// called a non-pool a pool) is red, false negative (a real pool was missed)
// is amber. The badge style uses a readable text color per background.
const findingBoxColor = computed(() =>
	finding.value?.kind === "false_positive"
		? BOX_COLOR_FALSE_POSITIVE
		: BOX_COLOR_FALSE_NEGATIVE,
);
const boxBadgeStyle = computed(() => ({
	background: findingBoxColor.value,
	color: finding.value?.kind === "false_positive" ? "#fff" : "#212529",
}));

// CSS transform for each panel's zoom wrapper: the 256-space framing converted
// to panel px (k = panelSize/256) as translate-then-scale about the panel's
// top-left. Same transform for all four variant panels. Recomputes when the
// framing changes (finding load) and when layout.size changes.
const zoomStyle = computed(() => {
	const k = layout.value.size / TILE_PX;
	const txPx = framing.value.tx * k;
	const tyPx = framing.value.ty * k;
	return {
		transform: `translate(${txPx}px, ${tyPx}px) scale(${framing.value.s})`,
		transformOrigin: "0 0",
	};
});

// Inverse of the zoom wrapper transform. The wrapper renders a tile-px point
// (x, y) at CSS position (x*k*s + txPx, y*k*s + tyPx) relative to the panel
// (k = panelSize/256, txPx/tyPx = framing translate in panel px), so the
// inverse subtracts the translate and divides by k*s. All pointer->tile
// conversions go through here so draw stays cursor-exact under the zoom.
function clientToTilePoint(
	cx: number,
	cy: number,
	panel: HTMLElement,
): { x: number; y: number } {
	const rect = panel.getBoundingClientRect();
	const k = rect.width / TILE_PX;
	const txPx = framing.value.tx * k;
	const tyPx = framing.value.ty * k;
	const inv = 1 / (k * framing.value.s);
	return {
		x: (cx - rect.left - txPx) * inv,
		y: (cy - rect.top - tyPx) * inv,
	};
}

function eventToTilePoint(
	e: MouseEvent,
	panel: HTMLElement,
): { x: number; y: number } {
	return clientToTilePoint(e.clientX, e.clientY, panel);
}

// Fetch existing pools intersecting the finding's imagery. Offset tiles show a
// one-tile region around a grid corner, which spans up to four slippy tiles, so
// query each and dedup by pool id, skipping the pool under scrutiny.
async function loadTilePools() {
	const f = finding.value;
	const t = displayTile.value;
	if (!f || !t || !f.itemId) {
		tilePools.value = [];
		return;
	}
	const z = t.z;
	const tiles: { x: number; y: number }[] = [];
	if (t.offset) {
		tiles.push(
			{ x: t.x, y: t.y },
			{ x: t.x + 1, y: t.y },
			{ x: t.x, y: t.y + 1 },
			{ x: t.x + 1, y: t.y + 1 },
		);
	} else {
		tiles.push({ x: t.x, y: t.y });
	}
	const seen = new Set<number>();
	const pools: VisionReviewTilePool[] = [];
	try {
		await Promise.all(
			tiles.map(async ({ x, y }) => {
				const detail = await apiClient.JSONGet<VisionReviewTileDetail>(
					`/api/vision/review/item/${encodeURIComponent(f.itemId!)}/${z}/${x}/${y}`,
				);
				for (const p of detail.pools ?? []) {
					if (seen.has(p.id)) continue;
					seen.add(p.id);
					if (f.poolId != null && p.id === f.poolId) continue;
					pools.push(p);
				}
			}),
		);
	} catch (e: unknown) {
		// Non-fatal: the review still works without the context overlay.
		console.warn("Failed to load nearby pools for finding", f.id, e);
	}
	tilePools.value = pools;
}

function onImgError(e: Event, variantId: string) {
	const target = e.target as HTMLImageElement;
	target.style.visibility = "hidden";
	brokenVariants.value.add(variantId);
}

// Ray-casting point-in-polygon test over a tile-pixel ring (the ring may be
// open or closed; the test treats it as cyclic).
function pointInRing(
	x: number,
	y: number,
	ring: { x: number; y: number }[],
): boolean {
	let inside = false;
	for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
		const xi = ring[i].x;
		const yi = ring[i].y;
		const xj = ring[j].x;
		const yj = ring[j].y;
		const intersects =
			yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
		if (intersects) inside = !inside;
	}
	return inside;
}

// Pick the topmost pool under a tile-pixel point (the flagged pool renders on
// top of context pools, so views are tested in reverse draw order). Hidden
// pools (their reshape draft has begun) are skipped.
function hitTestPool(x: number, y: number): number | null {
	const views = poolOutlineViews.value;
	for (let i = views.length - 1; i >= 0; i--) {
		const v = views[i];
		if (poolHidden(v.id)) continue;
		if (pointInRing(x, y, poolDisplayPts(v.id))) return v.id;
	}
	return null;
}

// Pick the topmost staged draft under a tile-pixel point. Staged drafts
// render above pool outlines, so when not drawing they are hit-tested FIRST:
// clicking one selects the draft (and its pts are directly movable), not the
// pool underneath. Hidden drafts (a replacement trace is in progress) are
// skipped, as are drafts replaced by a saved/removed pool (they are gone).
function hitTestStagedDraft(x: number, y: number): number | null {
	const shapes = stagedShapes.value;
	for (let i = shapes.length - 1; i >= 0; i--) {
		const s = shapes[i];
		if (stagedDraftHidden(s.uid)) continue;
		if (pointInRing(x, y, s.pts)) return s.uid;
	}
	return null;
}

function onTileMouseDown(e: MouseEvent) {
	if (e.button !== 0) return;
	const panel = e.currentTarget as HTMLElement | null;
	if (!panel) return;
	e.preventDefault();

	if (!drawMode.value) {
		// Selection + move: click a staged draft (topmost) or a pool polygon
		// to select it (click empty space to clear), and left-click-and-drag
		// the selection to move it. The drag is armed here but only moves once
		// the pointer travels past the threshold, so a plain click selects
		// without nudging. The finding box never participates.
		const tp = eventToTilePoint(e, panel);
		const draftUid = hitTestStagedDraft(tp.x, tp.y);
		if (draftUid !== null) {
			selectedDraftUid.value = draftUid;
			selectedPoolId.value = null;
			const shape = stagedShapes.value.find((s) => s.uid === draftUid);
			moveDrag.value = {
				kind: "draft",
				id: draftUid,
				panel,
				startClientX: e.clientX,
				startClientY: e.clientY,
				basePts: shape ? [...shape.pts] : [],
				moved: false,
			};
			window.addEventListener("mousemove", onMoveDragMove);
			window.addEventListener("mouseup", onMoveDragEnd);
			return;
		}
		const hit = hitTestPool(tp.x, tp.y);
		selectedPoolId.value = hit;
		selectedDraftUid.value = null;
		if (hit === null) return;
		moveDrag.value = {
			kind: "pool",
			id: hit,
			panel,
			startClientX: e.clientX,
			startClientY: e.clientY,
			basePts: poolDisplayPts(hit),
			moved: false,
		};
		window.addEventListener("mousemove", onMoveDragMove);
		window.addEventListener("mouseup", onMoveDragEnd);
		return;
	}

	// Draw mode: record the press. The release classifies it — a click (no
	// meaningful travel) starts or ends a shape, a drag is ignored. The window
	// draw listeners (already active while draw mode is on) do the rest.
	press = { panel, clientX: e.clientX, clientY: e.clientY, moved: false };
}

// Move drag: translate the dragged shape's ring by the pointer's travel in
// tile-pixel space (inverse zoom transform) so the outline follows the cursor
// live. A pool's translated ring is staged in stagedMoves (PUT on save); a
// staged draft's pts are translated in place (nothing is sent to the DB until
// S, which POSTs/PUTs whatever pts the draft holds then).
function onMoveDragMove(e: MouseEvent) {
	const d = moveDrag.value;
	if (!d) return;
	const dxCss = e.clientX - d.startClientX;
	const dyCss = e.clientY - d.startClientY;
	if (!d.moved && Math.hypot(dxCss, dyCss) < MOVE_DRAG_THRESHOLD) return;
	const rect = d.panel.getBoundingClientRect();
	const k = rect.width / TILE_PX;
	const inv = 1 / (k * framing.value.s);
	const dx = dxCss * inv;
	const dy = dyCss * inv;
	d.moved = true;
	const translated = d.basePts.map((p) => ({ x: p.x + dx, y: p.y + dy }));
	if (d.kind === "pool") {
		stagedMoves.value.set(d.id, translated);
		return;
	}
	// Staged draft: translate its pts in place. If the draft vanished (e.g.
	// discarded via D mid-drag) stop the drag.
	const idx = stagedShapes.value.findIndex((s) => s.uid === d.id);
	if (idx < 0) {
		window.removeEventListener("mousemove", onMoveDragMove);
		window.removeEventListener("mouseup", onMoveDragEnd);
		moveDrag.value = null;
		return;
	}
	stagedShapes.value[idx] = { ...stagedShapes.value[idx], pts: translated };
}

function onMoveDragEnd() {
	const d = moveDrag.value;
	window.removeEventListener("mousemove", onMoveDragMove);
	window.removeEventListener("mouseup", onMoveDragEnd);
	moveDrag.value = null;
	if (!d) return;
	// A click (no travel past the threshold) selected the shape already; a
	// move was staged live during the drag.
	if (!d.moved) return;
	if (d.kind === "pool") {
		showToast(
			"Pool moved",
			`Pool #${d.id} moved — press S to save the new geometry and resolve the finding.`,
		);
	} else {
		showToast(
			"Shape moved",
			`Staged shape moved — press S to save its new geometry and resolve the finding.`,
		);
	}
}

// Draw mode window mousemove: (1) a press travelling >= CLICK_MOVE_DIST css
// px becomes a drag, so its release will not start/end a shape; (2) while a
// trace is live, append a sample each time the pointer travels >= SAMPLE_STEP
// css px since the last appended point (measured in raw client coords, so the
// density is constant on screen at any zoom). Each sample is converted to a
// clamped tile pixel through the same inverse transform as every other
// pointer->tile conversion, so the outline stays cursor-exact under the zoom.
function onWindowDrawMove(e: MouseEvent) {
	const p = press;
	if (p && !p.moved) {
		const dx = e.clientX - p.clientX;
		const dy = e.clientY - p.clientY;
		if (dx * dx + dy * dy >= CLICK_MOVE_DIST * CLICK_MOVE_DIST) {
			p.moved = true;
		}
	}
	if (drawState !== "tracing") return;
	const panel = tracePanel;
	const last = lastSampleClient;
	if (!panel || !last) return;
	const dx = e.clientX - last.x;
	const dy = e.clientY - last.y;
	if (dx * dx + dy * dy < SAMPLE_STEP * SAMPLE_STEP) return;
	tracePts.value = [...tracePts.value, clampPos(eventToTilePoint(e, panel))];
	lastSampleClient = { x: e.clientX, y: e.clientY };
}

// Draw mode window mouseup: classify the armed press. A click with drawState
// idle STARTS a shape (the click point becomes its first point); a click with
// a live trace ENDS it — 3+ points stage the closed shape, fewer are silently
// ignored (a no-move double-click is start+end at the same point and leaves
// nothing behind). A drag (press.moved) does neither.
function onWindowDrawUp(e: MouseEvent) {
	const p = press;
	press = null;
	if (!p || p.moved || e.button !== 0) return;
	if (drawState === "idle") {
		// START: pin the first point (released position; within the click
		// threshold of the press point) and begin sampling on mousemove.
		tracePts.value = [clampPos(eventToTilePoint(e, p.panel))];
		lastSampleClient = { x: e.clientX, y: e.clientY };
		tracePanel = p.panel;
		drawState = "tracing";
		return;
	}
	// END: close + stage the trace.
	tracePanel = null;
	lastSampleClient = null;
	if (tracePts.value.length >= 3) {
		stageClosedShape();
	} else {
		// Not a real shape (e.g. the second click of a double-click): drop it
		// silently and stay in draw mode, ready for the next shape.
		tracePts.value = [];
		drawState = "idle";
	}
}

// Close the live trace and commit it: a new-pool trace appends a staged
// shape, a pool reshape appends a staged entry carrying the pool id (PUT on
// save), and a staged-draft redraw swaps that draft's pts in place (no new
// staged entry). The target always clears, so the next trace of the session
// is a brand-new pool. The session stays in draw mode for the next shape.
function stageClosedShape() {
	const target = draftTarget.value;
	const pts = [...tracePts.value];
	tracePts.value = [];
	drawState = "idle";
	draftTarget.value = null;
	if (target?.kind === "pool") {
		// Reshape: the replacement fully supersedes any earlier staged move
		// of the pool.
		stagedMoves.value.delete(target.poolId);
		stagedShapes.value = [
			...stagedShapes.value,
			{ uid: ++uidCounter, targetPoolId: target.poolId, pts },
		];
		const n = stagedShapes.value.length;
		showToast(
			"Shape staged",
			`Shape #${n} staged — replaces pool #${target.poolId}. Draw more, then S saves all; click a shape and press D to discard it.`,
		);
		return;
	}
	if (target?.kind === "draft") {
		// Redraw of a selected staged draft: same staged entry, pts swapped.
		const idx = stagedShapes.value.findIndex((s) => s.uid === target.uid);
		if (idx >= 0) {
			stagedShapes.value[idx] = { ...stagedShapes.value[idx], pts };
			showToast(
				"Shape redrawn",
				"Staged shape updated in place. Draw more, then S saves all; click a shape and press D to discard it.",
			);
			return;
		}
		// The targeted draft vanished mid-trace — fall back to a new pool.
	}
	stagedShapes.value = [
		...stagedShapes.value,
		{ uid: ++uidCounter, targetPoolId: null, pts },
	];
	const n = stagedShapes.value.length;
	showToast(
		"Shape staged",
		`Shape #${n} staged (new pool). Draw more, then S saves all; click a shape and press D to discard it.`,
	);
}

function addWindowDrawListeners() {
	window.addEventListener("mousemove", onWindowDrawMove);
	window.addEventListener("mouseup", onWindowDrawUp);
}

function removeWindowDrawListeners() {
	window.removeEventListener("mousemove", onWindowDrawMove);
	window.removeEventListener("mouseup", onWindowDrawUp);
}

// Cancel the live trace (Esc / V off / finding reset). Staged shapes and draw
// mode state are untouched by the caller as appropriate.
function cancelTrace() {
	tracePts.value = [];
	tracePanel = null;
	lastSampleClient = null;
	press = null;
	drawState = "idle";
}

// Remove a staged shape (D over its selection, or because the pool it was
// going to replace was deleted). Any selection or live redraw target pointing
// at it is cleared; a pool whose staged replacement is removed shows its old
// outline again automatically.
function removeStagedShape(uid: number): void {
	const idx = stagedShapes.value.findIndex((s) => s.uid === uid);
	if (idx < 0) return;
	stagedShapes.value = stagedShapes.value.filter((s) => s.uid !== uid);
	if (selectedDraftUid.value === uid) selectedDraftUid.value = null;
	if (draftTarget.value?.kind === "draft" && draftTarget.value.uid === uid) {
		draftTarget.value = null;
	}
}

function toggleDrawMode() {
	const f = finding.value;
	if (!f) return;
	if (drawMode.value) {
		// Turning draw mode off cancels any in-progress trace but KEEPS closed
		// staged shapes — they stay staged across V toggles until S.
		drawMode.value = false;
		cancelTrace();
		draftTarget.value = null;
		removeWindowDrawListeners();
		const n = stagedShapes.value.length;
		showToast(
			"Drawing stopped",
			n > 0
				? `${n} shape${n === 1 ? "" : "s"} still staged — S saves ${
						n === 1 ? "it" : "them"
					}, V draws more.`
				: "No shapes drawn yet — V draws, S saves staged shapes.",
		);
		return;
	}
	// Entering draw mode starts fresh traces but keeps any shapes staged from
	// earlier drawing sessions — they are only flushed by S.
	drawMode.value = true;
	tracePts.value = [];
	drawState = "idle";
	press = null;
	addWindowDrawListeners();
	showOutline.value = true;
	const selPool = selectedPoolId.value;
	const selDraft = selectedDraftUid.value;
	if (selPool !== null) {
		// Redraw the selected pool: the trace replaces it (PUT, keep id).
		draftTarget.value = { kind: "pool", poolId: selPool };
		showToast(
			"Reshape pool",
			`Redrawing pool #${selPool} — click to start its replacement, move the mouse to trace it, click again to finish. The old outline hides once the trace starts; S saves.`,
		);
		return;
	}
	if (selDraft !== null) {
		// Redraw the selected staged draft: the trace replaces its outline in
		// place (same staged entry, no extra pool).
		const idx = stagedShapes.value.findIndex((s) => s.uid === selDraft);
		draftTarget.value = { kind: "draft", uid: selDraft };
		showToast(
			"Redraw shape",
			idx >= 0
				? `Redrawing staged shape #${idx + 1} — click to start its replacement, move the mouse to trace it, click again to finish. Its old outline hides once the trace starts.`
				: "Redrawing the selected staged shape — click to start its replacement.",
		);
		return;
	}
	draftTarget.value = null;
	showToast(
		"Draw new pool",
		"Click to start a shape, move the mouse to trace it, click again to finish. Draw several shapes, then S saves them all. Staged shapes are clickable and draggable when not drawing.",
	);
}

function clearSelection() {
	selectedPoolId.value = null;
	selectedDraftUid.value = null;
}

// Header Draw-button copy: redraw a pool, redraw a staged draft, or draw a
// new pool, depending on the current selection.
const drawButtonTitle = computed(() => {
	if (drawMode.value) return "Stop drawing (V) — staged shapes are kept";
	if (selectedPoolId.value !== null) {
		return "Redraw (reshape) the selected pool\u2019s outline (V)";
	}
	if (selectedDraftUid.value !== null) {
		return "Redraw the selected staged shape\u2019s outline (V)";
	}
	return "Draw a new pool (V)";
});
const drawButtonLabel = computed(() => {
	if (drawMode.value) return "Stop drawing";
	if (selectedPoolId.value !== null) return "Reshape pool";
	if (selectedDraftUid.value !== null) return "Redraw shape";
	return "Draw polygon";
});

function toggleOutline() {
	showOutline.value = !showOutline.value;
}

function formatDate(iso: string): string {
	if (!iso) return "\u2014";
	return new Date(iso).toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

// The id of the finding currently shown, so the route watcher can distinguish
// a URL change we made ourselves (after a next-load) from a real navigation.
const loadedFindingId = ref<string | null>(null);

async function loadFinding(forceNext = false) {
	loading.value = true;
	error.value = null;
	brokenVariants.value = new Set();
	drawMode.value = false;
	removeWindowDrawListeners();
	cancelTrace();
	tracePts.value = [];
	stagedShapes.value = [];
	draftTarget.value = null;
	selectedPoolId.value = null;
	selectedDraftUid.value = null;
	stagedMoves.value.clear();
	deletedPoolIds.value = new Set();
	moveDrag.value = null;
	tilePools.value = [];
	framing.value = { s: 1, tx: 0, ty: 0 };
	try {
		const findingId = forceNext
			? undefined
			: (route.params.id as string | undefined);
		const url = findingId
			? `/api/vision/review/finding/${encodeURIComponent(findingId)}`
			: "/api/vision/review/finding/next";
		const result = await apiClient.JSONGet<ReviewFinding | null>(url);
		finding.value = result;
		computeFraming();
		if (result) {
			loadedFindingId.value = String(result.id);
			// A next-load has no id in the URL yet — write it so the finding is
			// shareable/bookmarkable.
			if (!findingId) {
				router.replace(`${BASE_PATH}/${result.id}`);
			}
		} else {
			loadedFindingId.value = null;
		}
		loadTilePools();
	} catch (e: unknown) {
		// Axios error or generic Error from the API client — read message if present.
		const err = e as {
			response?: { data?: { message?: string } };
			message?: string;
		};
		error.value =
			err?.response?.data?.message || err?.message || "Failed to load finding.";
		finding.value = null;
		loadedFindingId.value = null;
	} finally {
		loading.value = false;
	}
}

function nextFinding() {
	// Load the next pending finding regardless of any id in the URL.
	loadFinding(true);
}

function onKeydown(e: KeyboardEvent) {
	// Ignore when focus is in a text input / textarea.
	const target = e.target as HTMLElement | null;
	if (
		target &&
		(target.tagName === "INPUT" ||
			target.tagName === "TEXTAREA" ||
			target.isContentEditable)
	) {
		return;
	}
	// Reserve browser/modifier shortcuts (e.g. Cmd+S) for their normal use.
	if (e.ctrlKey || e.metaKey || e.altKey) return;

	switch (e.key.toLowerCase()) {
		case "o":
			toggleOutline();
			break;
		case "n":
			if (!loading.value) nextFinding();
			break;
		case "s":
			if (!loading.value && !saving.value && finding.value) saveFinding();
			break;
		case "d":
			if (
				!loading.value &&
				!saving.value &&
				!drawMode.value &&
				(selectedPoolId.value !== null || selectedDraftUid.value !== null)
			) {
				deleteSelection();
			}
			break;
		case "v":
			if (!loading.value && !saving.value && finding.value) toggleDrawMode();
			break;
		case "c":
			clearSelection();
			break;
		case "?":
			e.preventDefault();
			showShortcutsModal.value = true;
			break;
		case "escape":
			if (showShortcutsModal.value) {
				showShortcutsModal.value = false;
			} else if (drawMode.value && tracePts.value.length > 0) {
				// Cancel the live trace only — stay in draw mode; staged shapes
				// are untouched.
				cancelTrace();
				showToast(
					"Shape cancelled",
					"Current trace cleared — click to start a new shape.",
				);
			} else {
				clearSelection();
			}
			break;
	}
}

// Reverse the Web-Mercator projection used by projectToTile: take every point
// in the editable model (tile pixel space), project it back to geographic
// coordinates, and emit a closed Polygon ring. This reproduces exactly what
// the reviewer saw on the tile.
function pointsToGeometry(
	pts: { x: number; y: number }[],
	tile: { z: number; x: number; y: number; offset?: boolean },
): GeoJSON.Geometry {
	const z = tile.z;
	const scale = Math.pow(2, z);
	const ring: [number, number][] = pts.map((p) => {
		const x2 = tile.offset
			? (p.x - TILE_PX / 2) / TILE_PX + (tile.x + 1)
			: p.x / TILE_PX + tile.x;
		const y2 = tile.offset
			? (p.y - TILE_PX / 2) / TILE_PX + (tile.y + 1)
			: p.y / TILE_PX + tile.y;
		const lng = (x2 / scale) * 360 - 180;
		const t = Math.exp(Math.PI * (1 - (2 * y2) / scale));
		const lat = (2 * Math.atan(t) - Math.PI / 2) * (180 / Math.PI);
		return [lng, lat];
	});
	// Close the ring.
	ring.push([...ring[0]]);
	return { type: "Polygon", coordinates: [ring] };
}

async function saveFinding() {
	const f = finding.value;
	const t = displayTile.value;
	if (!f || !t) {
		showToast("Cannot save", "This finding has no tile to save.");
		return;
	}
	if (tracePts.value.length > 0) {
		showToast(
			"Finish the current shape first",
			"Finish or cancel the current shape before saving (staged shapes alone are fine).",
		);
		return;
	}
	saving.value = true;
	try {
		// 1. Staged drawn shapes: each reshapes its target pool (keep id +
		//    properties) or creates a brand-new pool.
		for (const s of stagedShapes.value) {
			const geometry = pointsToGeometry(s.pts, t);
			if (s.targetPoolId !== null) {
				await apiClient.JSONPut(`/api/vision/train/pool/${s.targetPoolId}`, {
					geometry,
				});
			} else {
				await apiClient.JSONPost("/api/vision/train/pool", {
					geometry,
					itemId: f.itemId ?? undefined,
				});
			}
		}
		// 2. Staged moves: persist each translated pool ring.
		for (const [poolId, pts] of Array.from(stagedMoves.value.entries())) {
			const geometry = pointsToGeometry(pts, t);
			await apiClient.JSONPut(`/api/vision/train/pool/${poolId}`, {
				geometry,
			});
		}
		// 3. Resolve the finding, then advance.
		await apiClient.JSONPut(`/api/vision/review/finding/${f.id}`, {
			status: "resolved",
		});
		stagedShapes.value = [];
		showToast("Finding resolved", "Changes saved and finding marked resolved.");
		await nextFinding();
	} catch (e: any) {
		showToast(
			"Save failed",
			e?.response?.data?.message || e?.message || "Failed to resolve finding.",
		);
	} finally {
		saving.value = false;
	}
}

// Delete the current selection immediately: an established pool is removed
// from the corpus (DB delete), a staged drawn shape is discarded from
// stagedShapes (no DB call — it has no row yet).
async function deleteSelection() {
	const draftUid = selectedDraftUid.value;
	if (draftUid !== null) {
		const f = finding.value;
		if (!f) return;
		removeStagedShape(draftUid);
		showToast(
			"Shape deleted",
			"The staged drawn shape was discarded. Press S when done to resolve the finding.",
		);
		return;
	}
	const poolId = selectedPoolId.value;
	const f = finding.value;
	if (!f || poolId === null) return;
	saving.value = true;
	try {
		await apiClient.JSONDelete(`/api/vision/train/pool/${poolId}`);
		// Clear any staged state tied to the deleted pool and stop rendering
		// it. The finding object itself is untouched (its display tile and
		// framing stay put); S resolves the finding when the review is done.
		stagedMoves.value.delete(poolId);
		if (selectedPoolId.value === poolId) selectedPoolId.value = null;
		if (
			draftTarget.value?.kind === "pool" &&
			draftTarget.value.poolId === poolId
		) {
			// The pool being redrawn is gone: cancel the trace and any target.
			draftTarget.value = null;
			cancelTrace();
			drawMode.value = false;
			removeWindowDrawListeners();
		}
		// Staged reshape shapes pointing at the deleted pool cannot be saved
		// (no PUT to a deleted pool): drop them and any selection on them.
		for (const s of [...stagedShapes.value]) {
			if (s.targetPoolId === poolId) removeStagedShape(s.uid);
		}
		deletedPoolIds.value.add(poolId);
		await loadTilePools();
		showToast(
			"Pool deleted",
			`Pool #${poolId} deleted. The finding is not resolved — press S when done to resolve it.`,
		);
	} catch (e: any) {
		showToast(
			"Delete failed",
			e?.response?.data?.message || e?.message || "Failed to delete pool.",
		);
	} finally {
		saving.value = false;
	}
}

watch(
	() => route.params.id,
	(newId) => {
		const fid = (newId as string | undefined) ?? null;
		// Ignore the URL write we just made after a next-load.
		if (fid === loadedFindingId.value) return;
		loadFinding();
	},
);

function clampPanelSize(s: number): number {
	return Math.min(MAX_PANEL, Math.max(MIN_PANEL, s));
}

// The card-header strip height: the panel cards are the flex items, so each
// flex line consumes size + header, not just size. Measured from the DOM once
// the cards render (header height is independent of panel size); the constant
// seeds the first pass before any card exists.
function headerHeight(): number {
	const el = panelsArea.value?.querySelector(".card-header");
	if (!el) return CARD_HEADER_H;
	return Math.ceil(el.getBoundingClientRect().height);
}

// Choose the best arrangement that fits the panels area (W/H = its client
// box): a 2x2 grid is preferred; a single row of four wins only on extremely
// short windows where 2x2 would force tiny panels. When neither fits at a
// usable (>= MIN_PANEL) size, show the error state instead of squinty or
// eye-straining panels. Sizes are clamped into [MIN_PANEL, MAX_PANEL].
function recomputeLayout() {
	const el = panelsArea.value;
	if (!el) return;
	const w = el.clientWidth;
	const h = el.clientHeight;
	const hdr = headerHeight();
	// 2x2: two panels per row with one gap between columns and rows; two
	// rows, each consuming size + card-header.
	const s2 = Math.min((w - PANEL_GAP) / 2, (h - 2 * hdr - PANEL_GAP) / 2);
	// Row of four: three gaps across; a single line, so the square panels are
	// bounded by the height minus one card-header.
	const s4 = Math.min((w - 3 * PANEL_GAP) / 4, h - hdr);
	if (s2 >= MIN_PANEL) {
		layout.value = { mode: "grid2x2", size: clampPanelSize(s2) };
	} else if (s4 >= MIN_PANEL) {
		layout.value = { mode: "row4", size: clampPanelSize(s4) };
	} else {
		layout.value = { mode: "error", size: 0 };
	}
}

let panelsObserver: ResizeObserver | null = null;

// Observe the panels area so the layout recomputes on window resizes and
// toolbar wraps. The element is recreated per finding (via :key) and on every
// load transition, so the watcher re-observes and recomputes immediately —
// which also covers the finding-load case the design calls out.
watch(
	panelsArea,
	(el, oldEl) => {
		if (oldEl && panelsObserver) panelsObserver.unobserve(oldEl);
		if (el) {
			if (!panelsObserver) panelsObserver = new ResizeObserver(recomputeLayout);
			panelsObserver.observe(el);
			recomputeLayout();
			// Re-run once the cards are actually painted so the header-height
			// measurement is exact, not the seed constant.
			requestAnimationFrame(recomputeLayout);
		}
	},
	{ flush: "post" },
);

onMounted(() => {
	window.addEventListener("keydown", onKeydown);
	loadFinding();
});

onUnmounted(() => {
	window.removeEventListener("keydown", onKeydown);
	removeWindowDrawListeners();
	panelsObserver?.disconnect();
	panelsObserver = null;
});
</script>
